import React, { Suspense } from 'react'
import { useQuery } from '@apollo/client'
import { FormattedMessage } from 'react-intl'
import { useMatch } from 'react-router'
import { Box, Flex, Text } from 'rebass/styled-components'

import { SpadeIcon } from '../Icon'

import { Site as GetSite } from '../../queries/NavigationQueries.gql'

import { trackEvent } from '../../utils/web-analytics-event'

import NavLink from './Link'
import { Item } from './Item'
import { Menu, MenuPopover, MenuContent, SwitchTrigger } from './Menu'
import UserMenu from './UserMenu'
import HelpMenu from './HelpMenu'
import { ChangelogMenu } from './ChangelogMenu'
import OrganisationMenu from './OrganisationMenu'
import SiteMenu from './SiteMenu'
import TeamMenu from './TeamMenu'
import Search from './Search'

import useBreakpoint from '../../hooks/useBreakpoint'
import truncate from '../../utils/smart-truncate'
import { useSession } from '../../providers/SessionProvider'

import CalibreIcon from '../Icon/Calibre'
import Button from '../Button/Button'
import ManageOrganisationLink from './OrganisationLink'

import styles from './Primary.module.css'

// eslint-disable-next-line no-undef
const CHANGELOG_API_URL = process.env.CHANGELOG_API_URL
const TRUNCATE_LENGTH = 30

export const Divider = ({ children, sx, ...props }) => (
  <Box
    as="span"
    aria-hidden="true"
    sx={{
      display: 'inline-block',
      width: '1px',
      height: '21px',
      ml: '4px',
      mr: '2px',
      backgroundColor: 'blue200',
      transform: 'rotate(20deg)',
      ...sx
    }}
    {...props}
  >
    {children}
  </Box>
)

const BrandLink = ({ full }) => {
  return (
    <Item>
      <NavLink href="/home" forceRefresh={true}>
        {full ? (
          <Suspense fallback={<div />}>
            <CalibreIcon />
          </Suspense>
        ) : (
          <SpadeIcon />
        )}
      </NavLink>
    </Item>
  )
}

const ManagementItems = ({ teamId, orgId, siteId }) => {
  const { currentUser, memberships } = useSession({
    teamId,
    orgId
  })

  const aboveMobile = useBreakpoint(0)

  const { email } = currentUser || {}

  const redirectParams = `?redirect_to=${window.location.pathname}`

  return email ? (
    <Flex sx={{ alignItems: 'center', ml: 'auto' }}>
      <Item sx={{ display: 'flex', mr: '15px' }}>
        <Suspense fallback={<div />}>
          <ManageOrganisationLink orgId={orgId} />
        </Suspense>
      </Item>
      <Item>{aboveMobile && memberships.length ? <Search orgId={orgId} teamId={teamId} siteId={siteId} /> : null}</Item>
      {/* Only render the ChangelogMenu if CHANGELOG_API_URL is defined */}
      {CHANGELOG_API_URL && (
        <Item>
          <ChangelogMenu apiUrl={CHANGELOG_API_URL} />
        </Item>
      )}
      <Item mr="15px">
        <HelpMenu />
      </Item>
      <Item key={email}>
        <UserMenu />
      </Item>
    </Flex>
  ) : (
    <Box ml="auto">
      {/* Only render the ChangelogMenu if CHANGELOG_API_URL is defined */}
      {CHANGELOG_API_URL && (
        <Item>
          <ChangelogMenu apiUrl={CHANGELOG_API_URL} />
        </Item>
      )}
      <Item>
        <Button variant="text" color="light-dark(white, var(--brand-grey-200))" href={`/sign-in${redirectParams}`}>
          <FormattedMessage id="navigation.links.sign_in" />
        </Button>
      </Item>
      <Item>
        <Button
          onClick={() => {
            trackEvent('Trial Signup (App Navigation)')
          }}
          href={`/sign-up${redirectParams}`}
        >
          <FormattedMessage id="navigation.links.sign_up" />
        </Button>
      </Item>
    </Box>
  )
}

const SiteLink = ({ orgId, teamId, siteId, forceRefresh, mobile, MenuComponent }) => {
  const aboveMobile = useBreakpoint(0)
  const { data, loading, error } = useQuery(GetSite, {
    variables: { orgId, teamId, siteId }
  })
  const { team } = data || {}
  const {
    site,
    sitesList: { edges: sitesEdges }
  } = team || { sitesList: { edges: [] } }
  const { name } = site || {}

  if (error) return null

  return (
    <>
      {aboveMobile ? (
        <Item>
          <Divider />
        </Item>
      ) : null}
      {mobile ? <BrandLink /> : null}

      {mobile || loading || sitesEdges.length > 1 ? (
        <Item key={siteId} data-qa="sitesDropdown">
          <Menu>
            <SwitchTrigger>{truncate(name || '…', TRUNCATE_LENGTH)}</SwitchTrigger>
            <MenuPopover>
              <MenuContent fullWidthMobile="true" level="md">
                <MenuComponent orgId={orgId} teamId={teamId} siteId={siteId} />
              </MenuContent>
            </MenuPopover>
          </Menu>
        </Item>
      ) : (
        <Item mr="15px">
          <NavLink to={`/teams/${teamId}/${siteId}`} forceRefresh={forceRefresh}>
            {truncate(name || '…', TRUNCATE_LENGTH)}
          </NavLink>
        </Item>
      )}
    </>
  )
}

const TeamLink = ({ orgId, teamId, forceRefresh, mobile, MenuComponent }) => {
  const { team, teams, memberships } = useSession({
    orgId,
    teamId
  })
  const { name } = team || {}

  return (
    <>
      {memberships.length < 2 || mobile ? <BrandLink /> : null}

      {mobile || teams.length > 1 || (teams.length > 0 && teamId != teams[0].slug) ? (
        <Item key={teamId} data-qa="teamsDropdown" sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <NavLink to={`/teams/${teamId}`} forceRefresh={forceRefresh}>
            {truncate(name, TRUNCATE_LENGTH)}
          </NavLink>
          <Menu>
            <SwitchTrigger />
            <MenuPopover align="center">
              <MenuContent fullWidthMobile="true" level="md">
                <MenuComponent orgId={orgId} teamId={teamId} />
              </MenuContent>
            </MenuPopover>
          </Menu>
        </Item>
      ) : (
        <Item mr="15px">
          <NavLink to={`/teams/${teamId}`} forceRefresh={forceRefresh}>
            {truncate(name, TRUNCATE_LENGTH)}
          </NavLink>
        </Item>
      )}
    </>
  )
}

const OrganisationLink = ({ staff, orgId, teamId, siteId, mobile, MenuComponent, forceRefresh }) => {
  const matchSetup = useMatch('/setup/:orgId?', {
    exact: false
  })
  const { membership, organisation, memberships, error } = useSession({
    orgId,
    teamId
  })

  if (!matchSetup && !mobile && !memberships.length)
    return (
      <Suspense fallback={<Item />}>
        <>
          <BrandLink />
          <Item>
            <Text variant="paragraph.sm" sx={{ color: 'white' }}>
              <FormattedMessage id="navigation.no_organisation.text" />
            </Text>
          </Item>
          <Item>
            <Button href="/setup">
              <FormattedMessage id="navigation.no_organisation.cta" />
            </Button>
          </Item>
        </>
      </Suspense>
    )

  if (!mobile && teamId && memberships.length < 2) return null

  const { slug, name: organisationName } =
    membership?.organisation ||
    (!(error?.type === 'Organisation') && organisation) ||
    (memberships.length && memberships[0].organisation)

  return (
    <>
      <BrandLink />
      {organisationName ? (
        <>
          {mobile || staff || memberships.length > 1 ? (
            <Item key={orgId} data-qa="organisationsDropdown">
              <Menu>
                <SwitchTrigger>{truncate(organisationName, TRUNCATE_LENGTH)}</SwitchTrigger>
                <MenuPopover>
                  <MenuContent fullWidthMobile="true" level="md">
                    <MenuComponent forceRefresh={forceRefresh} orgId={slug} teamId={teamId} siteId={siteId} />
                  </MenuContent>
                </MenuPopover>
              </Menu>
            </Item>
          ) : (
            <Item mr="15px">
              <NavLink to={`/organisations/${slug}`} forceRefresh={forceRefresh}>
                {truncate(organisationName, TRUNCATE_LENGTH)}
              </NavLink>
            </Item>
          )}
          {!mobile && teamId ? (
            <Item>
              <Divider />
            </Item>
          ) : null}
        </>
      ) : null}
    </>
  )
}

const PrimaryNavigation = ({ forceRefresh, orgId, teamId, siteId }) => {
  const { currentUser } = useSession({
    orgId,
    teamId
  })
  const aboveMobile = useBreakpoint(0)

  const showOrgLink = aboveMobile || (!aboveMobile && !teamId)
  const showTeamLink = teamId ? aboveMobile || (!aboveMobile && !siteId) : false
  const showSiteLink = !!siteId

  const CombinedMenu = ({ orgId, teamId, siteId }) => (
    <>
      <OrganisationMenu orgId={orgId} teamId={teamId} siteId={siteId} />
      {teamId ? <TeamMenu orgId={orgId} teamId={teamId} siteId={siteId} /> : null}
      {siteId ? <SiteMenu teamId={teamId} siteId={siteId} /> : null}
    </>
  )

  return (
    <Flex data-qa="primaryNavigation" className={styles.primary}>
      <Box flex={2}>
        <Flex as="ul" flex={1} alignItems="center" height="100%">
          {!currentUser ? (
            <BrandLink full={true} />
          ) : aboveMobile ? (
            <>
              {showOrgLink ? (
                <OrganisationLink
                  staff={currentUser?.staff}
                  forceRefresh={forceRefresh}
                  orgId={orgId}
                  teamId={teamId}
                  mobile={false}
                  MenuComponent={OrganisationMenu}
                />
              ) : null}
              {showTeamLink ? (
                <TeamLink
                  forceRefresh={forceRefresh}
                  orgId={orgId}
                  teamId={teamId}
                  mobile={false}
                  MenuComponent={TeamMenu}
                />
              ) : null}
              {showSiteLink ? (
                <SiteLink
                  forceRefresh={forceRefresh}
                  orgId={orgId}
                  teamId={teamId}
                  siteId={siteId}
                  mobile={false}
                  MenuComponent={SiteMenu}
                />
              ) : null}
            </>
          ) : (
            <>
              {showSiteLink ? (
                <SiteLink
                  forceRefresh={forceRefresh}
                  orgId={orgId}
                  teamId={teamId}
                  siteId={siteId}
                  mobile={true}
                  MenuComponent={CombinedMenu}
                />
              ) : showTeamLink ? (
                <TeamLink
                  forceRefresh={forceRefresh}
                  orgId={orgId}
                  teamId={teamId}
                  mobile={true}
                  MenuComponent={CombinedMenu}
                />
              ) : (
                <OrganisationLink
                  forceRefresh={forceRefresh}
                  orgId={orgId}
                  teamId={teamId}
                  mobile={true}
                  MenuComponent={CombinedMenu}
                />
              )}
            </>
          )}
        </Flex>
      </Box>
      <Box flex={1}>
        <Flex alignItems="center" height="100%">
          <ManagementItems orgId={orgId} teamId={teamId} siteId={siteId} />
        </Flex>
      </Box>
    </Flex>
  )
}

export default PrimaryNavigation
